import { FeedType, PublicationActionType, PublicationType } from "models/ApiEnums";
import { ICardProps } from "views/Timeline/Components/CardLib/ICardProps";
import { getPublicationSecondViewAction } from "views/Timeline/Components/FeedHelpers";

export type CardTopMessageMode = "default" | "topicOnly";

export class CardSetup {
    fullView: boolean;
    isDenouncement: boolean;
    inApprovalMode: boolean;
    hasBody: boolean;
    showBody: boolean;
    showBodyRegion: boolean;
    textExpanded: boolean;
    hasAccess: boolean;

    topMessageMode: CardTopMessageMode;

    reloadHandle: () => Promise<void>;
    deleteHandle: () => void;
    editHandle: () => void;
    titleHandle: () => void;
    publishHandle: () => void;
    visibilityHandle: () => void;

    constructor(props: ICardProps,
        forceBody: boolean,
        onProcessEdit: () => void,
        onProcessDelete: () => void,
        onProcessPublish: () => void,
        onProcessVisibility: () => void,
    ) {

        const {
            feedType,
            item,
        } = props;

        // Top Message
        this.topMessageMode =
            feedType == FeedType.TaskDenouncement
                || feedType == FeedType.TaskForYourAction
                || feedType == FeedType.TaskForYourApproval
                || feedType == FeedType.TaskRequestedByYou
                ? "topicOnly" : "default";

        // Está em modo aprovação?
        this.inApprovalMode =
            props.onApprovalRequest != null
            && item.action != null
            &&
            (
                feedType == FeedType.TaskForYourApproval
                || feedType == FeedType.TaskDenouncement
                || feedType == FeedType.TaskForYourAction
                || feedType == FeedType.TaskGeneralView
                || (feedType == FeedType.List && item.action.actionType == PublicationActionType.ApproveSuggestion)
            );

        // Tem acesso à publicação?
        this.hasAccess = item.permissions.hasAccess || this.inApprovalMode;

        // A Publicação define um CORPO (Conteúdo em TEXTO)?
        this.hasBody = item.publicationType != PublicationType.List && item.publicationType != PublicationType.Poll && item.publicationType != PublicationType.Survey;

        // Determina se deve exibir o CORPO da publicação.
        this.showBody =
            feedType != FeedType.TaskRequestedByYou
            && (!this.inApprovalMode || this.isDenouncement || forceBody);

        // Determina se a região de CORPO da publicação deve ser exibido.
        this.showBodyRegion = this.showBody || !this.hasAccess;

        // Determina se o CORPO está expandido
        this.textExpanded = this.inApprovalMode ? this.showBody : item.body.fullContentLoaded;

        // Está sendo exibido em página secundária?
        this.fullView = feedType == FeedType.Any;

        // É uma denúncia?
        this.isDenouncement = item.action != null
            && (item.action.actionType == PublicationActionType.ReportAbuseInComment || item.action.actionType == PublicationActionType.ReportAbuseInPublication);

        // Manuseio de eventos
        this.deleteHandle = item.permissions.canDelete && props.onRemovePublication
            ? onProcessDelete
            : null;

        this.editHandle = item.permissions.canEdit
            ? onProcessEdit
            : null;

        this.publishHandle = item.publication.isDraft === true
            ? onProcessPublish
            : null;

        this.titleHandle = !this.fullView && (item.permissions.canViewBody || item.publicationType == PublicationType.List)
            ? getPublicationSecondViewAction(item.publicationType, item.publicationId)
            : null;

        this.visibilityHandle = item.permissions.canClose && props.onChangePublicationVisibility
            ? onProcessVisibility
            : null;

        this.reloadHandle = props.onReloadPublication;
    }

}


export class QuestionCardSetup extends CardSetup {
    alwaysExpanded: boolean;
    questionRepassedToMe: boolean;

    constructor(props: ICardProps,
        forceBody: boolean,
        onProcessEdit: () => void,
        onProcessDelete: () => void,
        onProcessPublish: () => void,
        onProcessVisibility: () => void,
    ) {
        super(props, forceBody, onProcessEdit, onProcessDelete, onProcessPublish, onProcessVisibility);

        const {
            item,
        } = props;

        // No caso de questões, pode não haver CORPO.
        // Então quando está exibindo uma denúncia o Botão de Expandir não será exibido (pois não tem texto), mas o botão é necessário para visualizar a linha "Ações Padrões".
        // Neste caso iremos "forçar" uma string vazia para que o Botão de Expandir seja exibido.
        if (this.isDenouncement) {

            if (item.body.fullContent == null && item.body.summary == null && item.body.fullContentLoaded == null) {
                item.body.fullContent = item.body.summary = "";
                item.body.fullContentLoaded = false;
            }

            if (item.bestAnswer != null && item.bestAnswer.body.fullContent == null && item.bestAnswer.body.summary == null && item.bestAnswer.body.fullContentLoaded == null) {
                item.bestAnswer.body.fullContent = item.bestAnswer.body.summary = "";
                item.bestAnswer.body.fullContentLoaded = false;
            }
        }


        // É uma pergunta repassada para o usuário atual?
        this.questionRepassedToMe = item.action != null && item.action.forYourAnswer;

        // As perguntas podem não ter um CORPO (somente título).
        this.hasBody = item.body.summary != null || item.body.fullContent != null || item.body.fullContentLoaded === false;
        // Em fluxo de aprovação "normal", o CORPO exibido é o da resposta.
        if (item.action && item.action.actionType == PublicationActionType.ApproveWorkflow && item.bestAnswer != null)
            this.hasBody = item.bestAnswer.body.summary != null || item.bestAnswer.body.fullContent != null || item.bestAnswer.body.fullContentLoaded === false;

        // Em modo de aprovação, o texto da pergunta deve estar expandido.
        this.alwaysExpanded = this.hasBody && this.inApprovalMode;

        // Determina se o CORPO está expandido
        this.textExpanded = this.alwaysExpanded
            ? this.showBody
            : item.bestAnswer
                ? item.bestAnswer.body.fullContentLoaded
                : item.body.fullContentLoaded === true;
    }
}


export class AnswerCardSetup extends CardSetup {

    constructor(props: ICardProps,
        forceBody: boolean,
        onProcessEdit: () => void,
        onProcessDelete: () => void,
        onProcessPublish: () => void,
        onProcessVisibility: () => void,
    ) {
        super(props, forceBody, onProcessEdit, onProcessDelete, onProcessPublish, onProcessVisibility);

        const {
            item,
        } = props;

        // Workitem 591
        this.fullView = props.feedType == FeedType.Any || props.feedType == FeedType.Answers;

        this.titleHandle = item.permissions.canViewBody
            ? item.publicationType == PublicationType.Answer
                ? getPublicationSecondViewAction(item.publicationType, item.publicationId, item.publication.parentId)
                : getPublicationSecondViewAction(item.publicationType, item.publicationId)
            : null;
    }

}


export class PollCardSetup extends CardSetup {
    constructor(props: ICardProps,
        forceBody: boolean,
        onProcessEdit: () => void,
        onProcessDelete: () => void,
        onProcessPublish: () => void,
        onProcessVisibility: () => void,
    ) {
        super(props, forceBody, onProcessEdit, onProcessDelete, onProcessPublish, onProcessVisibility);

        // Determina se deve exibir o CORPO da publicação.
        this.showBody = false;

        // Determina se o CORPO está expandido
        this.textExpanded = null;

        // É uma denúncia?
        this.isDenouncement = false;
    }
}


export class CourseCardSetup extends CardSetup {
    constructor(props: ICardProps,
        forceBody: boolean,
        onProcessEdit: () => void,
        onProcessDelete: () => void,
        onProcessPublish: () => void,
        onProcessVisibility: () => void,
    ) {
        super(props, forceBody, onProcessEdit, onProcessDelete, onProcessPublish, onProcessVisibility);

        const {
            item,
        } = props;

        // É uma denúncia?
        this.isDenouncement = false;

        this.titleHandle = item.interaction.courseEnroll.enrolledOn
            ? getPublicationSecondViewAction(item.publicationType, item.publicationId)
            : null;
    }
}
